// ============================================================
// Dashboard — Square Code home surface.
//
// Editorial hero + compact KPI strip + row-based client list +
// OLIVIUM tile strip + upcoming deliverables. Designed against
// the command-center reference — tight spacing, editorial scale,
// mono kickers, row-based information density.
// ============================================================

import { COMMAND_CENTER_ROOT } from './CommandCenter/commandCenterRoutes'
import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import MetricCard, { type MetricTone } from '../components/MetricCard'
import ProjectRow from '../components/ProjectRow'
import EmptyState from '../components/EmptyState'
import { useProjects } from '../hooks/useProjects'
import { useAuditFeed } from '../hooks/useAuditFeed'
import { useSessions } from '../hooks/useSessions'
import { useChainIntegrity } from '../hooks/useChainIntegrity'
import { SHELL_TOKENS, text, timeAgo } from '../shell/styles'

interface OliviumTile {
  id: string
  label: string
  path: string
  glyph: string
  color: string
}

const OLIVIUM_TILES: OliviumTile[] = [
  { id: 'signal',    label: 'Signal',    path: '/square/olivium/feed',     glyph: '◉', color: SHELL_TOKENS.cyan },
  { id: 'presence',  label: 'Presence',  path: '/square/olivium/sessions', glyph: '◎', color: SHELL_TOKENS.green },
  { id: 'integrity', label: 'Integrity', path: '/square/olivium/chain',    glyph: '◈', color: SHELL_TOKENS.brand },
  { id: 'alerts',    label: 'Alerts',    path: '/square/olivium/alerts',   glyph: '◆', color: SHELL_TOKENS.red },
]

export default function Dashboard() {
  const navigate = useNavigate()
  const { projects, loading: projectsLoading } = useProjects()
  const { events, stats } = useAuditFeed()
  const { sessions } = useSessions()
  const chain = useChainIntegrity(events.length)

  const metrics = useMemo<Array<{ label: string; value: string | number; sub: string; tone?: MetricTone }>>(() => {
    const healthy = projects.filter((p) => (p.health_score ?? 0) >= 85).length
    const suspicious = sessions.filter((s) => s.status === 'suspicious').length
    return [
      { label: 'Projects',      value: projects.length,                       sub: 'active portfolio' },
      { label: 'Audit events',  value: stats.total,                           sub: `${stats.denials} denied`, tone: stats.denials ? 'warning' : 'default' },
      { label: 'Sessions',      value: sessions.length,                       sub: `${suspicious} suspicious`, tone: suspicious ? 'critical' : 'default' },
      { label: 'Health',        value: `${healthy}/${projects.length || 0}`, sub: chain.valid ? 'chain verified' : 'integrity issue', tone: chain.valid ? 'default' : 'critical' },
    ]
  }, [projects, sessions, stats, chain.valid])

  const upcoming = projects.slice(0, 6)

  return (
    <div style={{ maxWidth: 920, margin: '0 auto' }}>
      {/* Editorial hero */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-end',
          marginBottom: 26,
          gap: 16,
        }}
      >
        <div>
          <div
            style={{
              ...text.kicker,
              fontSize: 10,
              letterSpacing: '0.28em',
              color: SHELL_TOKENS.inkFaint,
              marginBottom: 6,
            }}
          >
            SQUARE CODE
          </div>
          <div style={text.editorial}>Command Center</div>
          <div
            style={{
              fontSize: 11,
              color: SHELL_TOKENS.inkMuted,
              marginTop: 6,
              fontFamily: 'var(--font-mono)',
              letterSpacing: '0.02em',
            }}
          >
            governed private operator system · OLIVIUM trust layer
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8 }}>
          {/* PHASE_2K deployment promotion: Command Center V2 is the primary
              command center. Prominent entry from the landing (we do NOT redirect
              / or /square, to avoid breaking the login/landing flow). */}
          <button
            type="button"
            onClick={() => navigate(COMMAND_CENTER_ROOT)}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer',
              padding: '9px 16px', borderRadius: 10, whiteSpace: 'nowrap',
              background: `${SHELL_TOKENS.brand}1f`, border: `1px solid ${SHELL_TOKENS.brand}55`,
              color: SHELL_TOKENS.brand, fontSize: 12.5, fontWeight: 800, fontFamily: 'inherit',
            }}
          >
            ◆ Open Command Center →
          </button>
          <div style={{ display: 'flex', gap: 6 }}>
            <StatChip label="alerts" value={stats.criticals + stats.warnings} accent={SHELL_TOKENS.amber} />
            <StatChip
              label="active"
              value={projects.length}
              accent={SHELL_TOKENS.green}
              pulseDot
            />
          </div>
        </div>
      </div>

      {/* KPI strip */}
      <section
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
          gap: 10,
          marginBottom: 24,
        }}
      >
        {metrics.map((m) => (
          <MetricCard key={m.label} {...m} />
        ))}
      </section>

      {/* Client portfolio — row list */}
      <section style={{ marginBottom: 26 }}>
        <Kicker>CLIENT PORTFOLIO</Kicker>
        {projectsLoading ? (
          <EmptyState title="Loading projects" body="Pulling portfolio into the shell." />
        ) : projects.length === 0 ? (
          <EmptyState
            title="No projects yet"
            body="Add rows to sq_projects, or run in demo mode to see a sample portfolio."
          />
        ) : (
          projects.map((p) => (
            <ProjectRow
              key={p.id}
              project={p}
              onSelect={(proj) => navigate(`/square/projects/${proj.id}`)}
            />
          ))
        )}
      </section>

      {/* OLIVIUM workspace strip */}
      <section style={{ marginBottom: 26 }}>
        <Kicker>SECURITY · OLIVIUM</Kicker>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
            gap: 8,
          }}
        >
          {OLIVIUM_TILES.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => navigate(t.path)}
              style={{
                padding: '16px 12px',
                borderRadius: 10,
                textAlign: 'center',
                background: SHELL_TOKENS.surface,
                border: `1px solid ${SHELL_TOKENS.border}`,
                cursor: 'pointer',
                color: SHELL_TOKENS.ink,
                fontFamily: 'inherit',
                transition: 'border-color 200ms, background 200ms',
              }}
            >
              <div style={{ fontSize: 18, color: `${t.color}aa`, marginBottom: 4, fontFamily: 'var(--font-brand)' }}>
                {t.glyph}
              </div>
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: t.color,
                  letterSpacing: '0.04em',
                }}
              >
                {t.label}
              </div>
            </button>
          ))}
        </div>
      </section>

      {/* Tri-corder spine — GitHub + GitLab */}
      <section style={{ marginBottom: 26 }}>
        <Kicker>CODE SPINE · TRI-CORDER</Kicker>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
            gap: 10,
          }}
        >
          <SpineCard
            provider="GitHub"
            role="Code spine · OAuth login · CI/CD"
            glyph="◉"
            status="canonical"
            onClick={() => navigate('/square/control/integrations')}
          />
          <SpineCard
            provider="GitLab"
            role="Collaboration · Delivery · Tri-corder"
            glyph="◎"
            status="active"
            onClick={() => navigate('/square/control/integrations')}
          />
        </div>
        <div
          style={{
            marginTop: 8,
            display: 'flex',
            gap: 16,
            fontSize: 9,
            color: SHELL_TOKENS.inkFaint,
            fontFamily: 'var(--font-mono)',
            letterSpacing: '0.08em',
          }}
        >
          <span>
            LINKED REPOS:{' '}
            <span style={{ color: SHELL_TOKENS.ink, fontWeight: 700 }}>
              {projects.filter((p: any) => p.github_repo_url || p.gitlab_url).length}/{projects.length}
            </span>
          </span>
          <span>
            GITHUB:{' '}
            <span style={{ color: SHELL_TOKENS.green, fontWeight: 700 }}>
              {projects.filter((p: any) => p.github_repo_url).length}
            </span>
          </span>
          <span>
            GITLAB:{' '}
            <span style={{ color: SHELL_TOKENS.cyan, fontWeight: 700 }}>
              {projects.filter((p: any) => p.gitlab_url).length}
            </span>
          </span>
        </div>
      </section>

      {/* Upcoming deliverables */}
      <section>
        <Kicker>UPCOMING DELIVERABLES</Kicker>
        {upcoming.length === 0 ? (
          <EmptyState title="Nothing scheduled" body="Deliverables surface here once sq_projects has rows." />
        ) : (
          upcoming.map((p) => (
            <div
              key={p.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '9px 12px',
                borderRadius: 6,
                marginBottom: 3,
                background: SHELL_TOKENS.surface,
                border: `1px solid ${SHELL_TOKENS.border}`,
                borderLeft: `2px solid ${SHELL_TOKENS.brand}`,
              }}
            >
              <span
                style={{
                  fontSize: 9,
                  fontWeight: 800,
                  color: SHELL_TOKENS.brand,
                  letterSpacing: '0.06em',
                  fontFamily: 'var(--font-mono)',
                  minWidth: 90,
                }}
              >
                {p.name || p.client_name}
              </span>
              <span style={{ fontSize: 10, color: SHELL_TOKENS.inkMuted, flex: 1 }}>
                {p.status || 'active'} · updated {timeAgo(p.updated_at)}
              </span>
              <span
                style={{
                  fontSize: 9,
                  color: SHELL_TOKENS.inkFaint,
                  fontFamily: 'var(--font-mono)',
                }}
              >
                {(p.health_score ?? 0)}% health
              </span>
            </div>
          ))
        )}
      </section>
    </div>
  )
}

// ----------------------------------------------------------------
// Local helpers
// ----------------------------------------------------------------

function Kicker({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        ...text.kicker,
        fontSize: 9,
        letterSpacing: '0.24em',
        color: SHELL_TOKENS.inkWhisper,
        marginBottom: 10,
      }}
    >
      {children}
    </div>
  )
}

function SpineCard({
  provider,
  role,
  glyph,
  status,
  onClick,
}: {
  provider: string
  role: string
  glyph: string
  status: 'canonical' | 'active' | 'planned' | 'not configured'
  onClick?: () => void
}) {
  const accent =
    status === 'canonical' ? SHELL_TOKENS.green
    : status === 'active' ? SHELL_TOKENS.cyan
    : SHELL_TOKENS.inkFaint

  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        padding: '14px 16px',
        borderRadius: 12,
        background: SHELL_TOKENS.surface,
        border: `1px solid ${SHELL_TOKENS.border}`,
        cursor: 'pointer',
        color: SHELL_TOKENS.ink,
        fontFamily: 'inherit',
        textAlign: 'left',
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        transition: 'border-color 200ms',
      }}
    >
      <div
        style={{
          width: 36,
          height: 36,
          borderRadius: 10,
          background: `${accent}14`,
          border: `1px solid ${accent}30`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 16,
          color: accent,
          fontFamily: 'var(--font-brand)',
          flexShrink: 0,
        }}
      >
        {glyph}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: SHELL_TOKENS.ink }}>
          {provider}
        </div>
        <div
          style={{
            fontSize: 10,
            color: SHELL_TOKENS.inkMuted,
            fontFamily: 'var(--font-mono)',
            marginTop: 2,
          }}
        >
          {role}
        </div>
      </div>
      <span
        style={{
          fontSize: 9,
          color: accent,
          fontFamily: 'var(--font-mono)',
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          fontWeight: 700,
          flexShrink: 0,
        }}
      >
        {status}
      </span>
    </button>
  )
}

function StatChip({
  label,
  value,
  accent,
  pulseDot,
}: {
  label: string
  value: number | string
  accent: string
  pulseDot?: boolean
}) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        padding: '6px 12px',
        borderRadius: 8,
        background: SHELL_TOKENS.surface,
        border: `1px solid ${SHELL_TOKENS.border}`,
      }}
    >
      {pulseDot && (
        <span
          style={{
            width: 6,
            height: 6,
            borderRadius: '50%',
            background: accent,
            boxShadow: `0 0 8px ${accent}88`,
          }}
        />
      )}
      <span
        style={{
          fontSize: 11,
          fontWeight: 700,
          color: accent,
          fontFamily: 'var(--font-mono)',
        }}
      >
        {value}
      </span>
      <span
        style={{
          fontSize: 9,
          color: SHELL_TOKENS.inkFaint,
          letterSpacing: '0.1em',
          textTransform: 'uppercase',
          fontFamily: 'var(--font-mono)',
        }}
      >
        {label}
      </span>
    </div>
  )
}
